import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import UserService from '../../Services/UserService';
import { get, put } from '../../Services/API';
import ServiceOfferService from '../../Services/ServiceOfferService';
import TripClaimsRefusalAlert from '../../Components/TripClaims/TripClaimsRefusalAlert';
import { announceTripClaimsRefused, tripClaimsRefusalOf } from '../../Utils/tripClaims';
import {
  DEFAULT_TIMEZONE, toUtcIsoString, formatDateTime,
} from '../../Utils/timezone';
import { PICKUP_HUNGARY_PARTNER_ID } from '../../config/config';

dayjs.extend(utc);
dayjs.extend(tz);

function Home({ session }) {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const [currentMonth, setCurrentMonth] = useState(() => dayjs().tz(DEFAULT_TIMEZONE));
  const [bookingData, setBookingData] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [completingIds, setCompletingIds] = useState(new Set());
  const [pendingFilter, setPendingFilter] = useState('review'); // 'review' or 'all'

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/orders?quick_search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const userService = UserService.getInstance();

  const quickActions = [
    {
      to: '/stats',
      icon: 'fa-chart-bar',
      title: 'Stats',
      description: 'View analytics and reports',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
      to: '/trips',
      icon: 'fa-route',
      title: 'Daily Overview',
      description: 'Manage and track trip schedules',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      to: '/orders',
      icon: 'fa-shopping-cart',
      title: 'Orders',
      description: 'View and process customer orders',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      to: '/service-offers',
      icon: 'fa-file-invoice',
      title: 'Service Offers',
      description: 'Browse and manage service offers',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
      to: '/events',
      icon: 'fa-calendar-alt',
      title: 'Events',
      description: 'View and manage events',
      gradient: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
    },
    ...(userService.canAccessModule('CanInteractWithPricingEngine') ? [{
      to: '/pricing',
      icon: 'fa-calculator',
      title: 'Pricing Engine',
      description: 'Configure pricing rules',
      gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    }] : []),
    {
      to: '/partner-sync',
      icon: 'fa-sync',
      title: 'Sync',
      description: 'Sync data with external partners',
      gradient: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    },
  ];

  const generateCalendarDays = (month) => {
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');

    // Find Monday of the week containing the first day (day() returns 0=Sun)
    const firstDayOfWeek = startOfMonth.day();
    const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    const calendarStart = startOfMonth.subtract(daysToSubtract, 'day');

    // Find Sunday of the week containing the last day
    const lastDayOfWeek = endOfMonth.day();
    const daysToAdd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
    const calendarEnd = endOfMonth.add(daysToAdd, 'day');

    const days = [];
    let d = calendarStart;
    while (d.isBefore(calendarEnd) || d.isSame(calendarEnd, 'day')) {
      days.push(d);
      d = d.add(1, 'day');
    }
    return days;
  };

  const fetchCalendarData = useCallback(async (month) => {
    setCalendarLoading(true);
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    const targetMonthKey = month.format('YYYY-MM');

    const filters = {
      page: 1,
      pageSize: 2000,
      date: [toUtcIsoString(startOfMonth), toUtcIsoString(endOfMonth)],
      sort: { date_datetime: 'asc' },
      // This calendar counts a day's WORK. The tally below is
      // `if experience … else transfers += 1`, so without this an expense
      // would be counted as a phantom transfer — a day with one fuel
      // receipt and no trips would show as a working day.
      exclude_booking_types: ['expense'],
    };

    try {
      const aggregated = {};
      const response = await get('/bookings', { filters });
      const docs = response.data?.data?.documents || [];

      docs.forEach((booking) => {
        // Only count confirmed bookings (matches Trips page default filter)
        if (booking.is_archived || booking.status !== 'Confirmed') {
          return;
        }
        const dateKey = formatDateTime(booking.date_datetime, 'YYYY-MM-DD');
        // Skip bookings matched via return_date_datetime whose pickup is outside this month
        if (!dateKey || !dateKey.startsWith(targetMonthKey)) {
          return;
        }
        if (!aggregated[dateKey]) {
          aggregated[dateKey] = { transfers: 0, experiences: 0, reminders: 0 };
        }
        if (booking.booking_type === 'experience') {
          aggregated[dateKey].experiences += 1;
        } else {
          aggregated[dateKey].transfers += 1;
        }
        (booking.notifications || []).forEach((reminder) => {
          if (!reminder.is_completed && reminder.due_date) {
            const reminderKey = formatDateTime(reminder.due_date, 'YYYY-MM-DD');
            if (reminderKey) {
              if (!aggregated[reminderKey]) {
                aggregated[reminderKey] = { transfers: 0, experiences: 0, reminders: 0 };
              }
              aggregated[reminderKey].reminders += 1;
            }
          }
        });
      });

      setBookingData(aggregated);
    } catch {
      // silently fail — calendar is a non-critical summary
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendarData(currentMonth);
  }, [currentMonth, fetchCalendarData]);

  // Fetch past confirmed bookings (pending completion)
  const fetchPendingBookings = useCallback(async () => {
    setPendingLoading(true);
    try {
      const now = dayjs().tz(DEFAULT_TIMEZONE);
      // Look back 7 days for past confirmed bookings
      const startDate = now.subtract(7, 'day').startOf('day');
      const endDate = now; // up to now (past bookings only)

      const filters = {
        page: 1,
        pageSize: 100,
        date: [toUtcIsoString(startDate), toUtcIsoString(endDate)],
        status: ['Confirmed'],
        sort: { date_datetime: 'desc' },
        // "Past confirmed bookings waiting to be completed". An expense is
        // ALWAYS past its date — costs are recorded after the money has
        // gone — so every one of them would queue up here for ever with a
        // Mark Complete button nobody should press.
        exclude_booking_types: ['expense'],
      };

      const response = await get('/bookings', { filters });
      const docs = response.data?.data?.documents || [];
      // Only show transfers that are past their pickup time
      const pastBookings = docs.filter((b) => {
        if (b.is_archived || b.booking_type === 'experience') return false;
        return dayjs(b.date_datetime).isBefore(now);
      });
      setPendingBookings(pastBookings);
    } catch {
      // silently fail
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingBookings();
  }, [fetchPendingBookings]);

  const handleMarkComplete = async (bookingId, skipReview) => {
    setCompletingIds((prev) => new Set([...prev, bookingId]));
    try {
      const payload = { _id: bookingId, status: 'Completed' };
      if (skipReview) payload.skip_review_email = true;
      await put('/bookings', payload);
      // eslint-disable-next-line no-underscore-dangle
      setPendingBookings((prev) => prev.filter((b) => b._id !== bookingId));
    } catch (err) {
      // Trip claims still waiting for review: shown with the way to them.
      const refusal = tripClaimsRefusalOf(err);
      // eslint-disable-next-line no-underscore-dangle
      const booking = pendingBookings.find((b) => b._id === bookingId);
      if (refusal) announceTripClaimsRefused(booking, refusal);
      // eslint-disable-next-line no-alert
      else alert(err.response?.data?.message || 'Failed to mark booking as completed');
    } finally {
      setCompletingIds((prev) => {
        const next = new Set(prev);
        next.delete(bookingId);
        return next;
      });
    }
  };

  const getTimeSincePickup = (dateStr) => {
    const pickup = dayjs(dateStr);
    const now = dayjs();
    const hours = now.diff(pickup, 'hour');
    const minutes = now.diff(pickup, 'minute') % 60;
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h ago`;
    }
    return `${hours}h ${minutes}m ago`;
  };

  // Calculate time until midnight auto-complete (Budapest time)
  const getTimeUntilAutoComplete = () => {
    const now = dayjs().tz(DEFAULT_TIMEZONE);
    const midnight = now.endOf('day');
    const hours = midnight.diff(now, 'hour');
    const minutes = midnight.diff(now, 'minute') % 60;
    return `${hours}h ${minutes}m`;
  };

  useEffect(() => {
    const fetchOffers = async () => {
      setOffersLoading(true);
      try {
        const response = await ServiceOfferService.fetchAll();
        setOffers(response?.data?.data || []);
      } catch {
        // silently fail
      } finally {
        setOffersLoading(false);
      }
    };
    fetchOffers();
  }, []);

  // Merge booking data with offer validUntil dates for the calendar
  const calendarData = useMemo(() => {
    // Deep-copy per-day objects to avoid mutating bookingData state
    // (shallow spread shares inner object references, causing double-counting in StrictMode)
    const combined = Object.fromEntries(
      Object.entries(bookingData).map(([k, v]) => [k, { ...v }]),
    );
    const activeOfferStatuses = ['sent', 'quoting', 'draft'];
    offers.forEach((offer) => {
      // Only show expiry for active offers (skip expired, rejected, accepted)
      if (!activeOfferStatuses.includes(offer.status)) {
        return;
      }
      if (offer.validUntil) {
        const key = offer.validUntil.slice(0, 10); // ensure YYYY-MM-DD
        if (!combined[key]) {
          combined[key] = {
            transfers: 0, experiences: 0, reminders: 0, expiring: 0,
          };
        }
        combined[key].expiring = (combined[key].expiring || 0) + 1;
      }
    });
    return combined;
  }, [bookingData, offers]);

  // Filter pending bookings: "review" = only PH partner bookings, "all" = everything
  const filteredPendingBookings = useMemo(
    () => (pendingFilter === 'review'
      ? pendingBookings.filter((b) => b.partner_id === PICKUP_HUNGARY_PARTNER_ID)
      : pendingBookings),
    [pendingBookings, pendingFilter],
  );

  // Offers table: Sent, Quoting, Draft
  const activeOffers = useMemo(
    () => offers.filter((o) => ['sent', 'quoting', 'draft'].includes(o.status)),
    [offers],
  );

  const getStatusBadge = (status) => {
    const map = {
      lead: { bg: 'warning', text: 'dark', label: 'Lead' },
      quoting: { bg: 'info', label: 'Quoting' },
      draft: { bg: 'secondary', label: 'Draft' },
      sent: { bg: 'primary', label: 'Sent' },
    };
    const cfg = map[status] || map.draft;
    return <Badge bg={cfg.bg} text={cfg.text || undefined}>{cfg.label}</Badge>;
  };

  const formatOfferDate = (dateStr) => {
    if (!dateStr) return '—';
    return dayjs(dateStr).format('DD MMM YYYY');
  };

  const roundBtnStyle = { width: '34px', height: '34px', padding: 0 };
  const roundBtnClass = 'rounded-circle d-inline-flex align-items-center justify-content-center';

  // eslint-disable-next-line no-underscore-dangle
  const renderCompleteActions = (booking) => {
    const isPH = booking.partner_id === PICKUP_HUNGARY_PARTNER_ID;
    // eslint-disable-next-line no-underscore-dangle
    if (completingIds.has(booking._id)) {
      return <i className="fa fa-spin fa-spinner text-muted" />;
    }
    if (isPH && booking.email_address) {
      return (
        <div className="d-flex gap-1 justify-content-center">
          <button
            type="button"
            className={`btn btn-success ${roundBtnClass}`}
            style={roundBtnStyle}
            // eslint-disable-next-line no-underscore-dangle
            onClick={() => handleMarkComplete(booking._id, false)}
            title="Complete with review email"
          >
            <i className="fa fa-envelope" />
          </button>
          <button
            type="button"
            className={`btn btn-outline-secondary ${roundBtnClass}`}
            style={roundBtnStyle}
            // eslint-disable-next-line no-underscore-dangle
            onClick={() => handleMarkComplete(booking._id, true)}
            title="Complete without review email"
          >
            <i className="fa fa-envelope-open" />
          </button>
        </div>
      );
    }
    if (isPH && !booking.email_address) {
      return (
        <div className="d-flex gap-1 justify-content-center">
          <button
            type="button"
            className={`btn btn-warning ${roundBtnClass}`}
            style={roundBtnStyle}
            // eslint-disable-next-line no-underscore-dangle
            onClick={() => handleMarkComplete(booking._id, false)}
            title="Complete (email missing — no review email)"
          >
            <i className="fa fa-exclamation-triangle" />
          </button>
        </div>
      );
    }
    return (
      <button
        type="button"
        className={`btn btn-success ${roundBtnClass}`}
        style={roundBtnStyle}
        // eslint-disable-next-line no-underscore-dangle
        onClick={() => handleMarkComplete(booking._id, false)}
        title="Complete"
      >
        <i className="fa fa-check" />
      </button>
    );
  };

  const calendarDays = generateCalendarDays(currentMonth);
  const today = dayjs().tz(DEFAULT_TIMEZONE).format('YYYY-MM-DD');

  return (
    <div className="home-page">
      {/* Hero Section */}
      <div className="hero-section mb-5">
        <div className="row align-items-center">
          <div className="col-lg-8">
            <h1 className="display-4 fw-bold mb-3">
              Welcome back,
              {' '}
              <span className="hero-highlight">{session.givenName}</span>
              !
            </h1>
            <p className="lead text-muted mb-4">
              DEVOLOGY.IO Order Management System
            </p>
            <div className="company-badge mb-4">
              <i className="fa fa-building me-2" />
              <strong>Pickup Hungary</strong>
            </div>

            {/* Quick Search */}
            <form onSubmit={handleSearch} className="quick-search-form">
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa fa-search text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Quick search orders, trips, bookings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={!searchQuery.trim()}
                >
                  Search
                </button>
              </div>
              <small className="text-muted d-block mt-2">
                <i className="fa fa-lightbulb me-1" />
                Try searching by order ID, booking reference, or customer name
              </small>
            </form>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-chart-line fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions + Monthly Overview side by side */}
      <div className="row g-4 align-items-start">
        {/* Quick Actions — left on desktop, below calendar on mobile */}
        <div className="col-12 col-xl-5 order-2 order-xl-1">
          <div className="quick-actions-section">
            <h3 className="mb-4 fw-bold">Quick Actions</h3>
            <div className="row g-3">
              {quickActions.map((action) => (
                <div key={action.to} className="col-12">
                  <NavLink to={action.to} className="quick-action-card quick-action-card--compact">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body d-flex align-items-center gap-3 py-3">
                        <div
                          className="quick-action-icon-sm flex-shrink-0"
                          style={{ background: action.gradient }}
                        >
                          <i className={`fa ${action.icon}`} />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-semibold text-dark lh-1 mb-1">{action.title}</div>
                          <div className="text-muted small text-truncate">{action.description}</div>
                        </div>
                        <div className="card-arrow flex-shrink-0">
                          <i className="fa fa-arrow-right" />
                        </div>
                      </div>
                    </div>
                  </NavLink>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Monthly Overview — right on desktop, above quick actions on mobile */}
        <div className="col-12 col-xl-7 order-1 order-xl-2">
          <div className="calendar-section">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h3 className="fw-bold mb-0">Monthly Overview</h3>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setCurrentMonth((m) => m.subtract(1, 'month'))}
                >
                  <i className="fa fa-chevron-left" />
                </button>
                <span className="calendar-month-label">
                  {currentMonth.format('MMMM YYYY')}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setCurrentMonth((m) => m.add(1, 'month'))}
                >
                  <i className="fa fa-chevron-right" />
                </button>
              </div>
            </div>

            {calendarLoading ? (
              <div className="text-center py-5">
                <i className="fa fa-spin fa-spinner fa-2x text-muted" />
              </div>
            ) : (
              <div className="calendar-grid">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                  <div key={d} className="calendar-header-cell">{d}</div>
                ))}
                {calendarDays.map((day) => {
                  const key = day.format('YYYY-MM-DD');
                  const isCurrentMonth = day.month() === currentMonth.month();
                  const isToday = key === today;
                  const data = calendarData[key];

                  return (
                    <div
                      key={key}
                      className={[
                        'calendar-day-cell',
                        !isCurrentMonth ? 'other-month' : '',
                        isToday ? 'today' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <span className="day-number">{day.date()}</span>
                      {data && (
                        <div className="calendar-badges">
                          {data.transfers > 0 && (
                            <span className="badge bg-primary calendar-badge" title="Transfers">
                              <i className="fa fa-route me-1" />
                              {data.transfers}
                            </span>
                          )}
                          {data.experiences > 0 && (
                            <span className="badge bg-success calendar-badge" title="Experiences">
                              <i className="fa fa-map-marked-alt me-1" />
                              {data.experiences}
                            </span>
                          )}
                          {data.reminders > 0 && (
                            <span className="badge bg-warning text-dark calendar-badge" title="Pending reminders">
                              <i className="fa fa-bell me-1" />
                              {data.reminders}
                            </span>
                          )}
                          {data.expiring > 0 && (
                            <span className="badge bg-danger calendar-badge" title="Offers expiring">
                              <i className="fa fa-file-invoice me-1" />
                              {data.expiring}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="d-flex gap-3 mt-3 flex-wrap">
              <small className="text-muted d-flex align-items-center gap-1">
                <span className="badge bg-primary">
                  <i className="fa fa-route" />
                </span>
                Transfers
              </small>
              <small className="text-muted d-flex align-items-center gap-1">
                <span className="badge bg-success">
                  <i className="fa fa-map-marked-alt" />
                </span>
                Experiences
              </small>
              <small className="text-muted d-flex align-items-center gap-1">
                <span className="badge bg-warning text-dark">
                  <i className="fa fa-bell" />
                </span>
                Pending reminders
              </small>
              <small className="text-muted d-flex align-items-center gap-1">
                <span className="badge bg-danger">
                  <i className="fa fa-file-invoice" />
                </span>
                Offers expiring
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Completion Section */}
      <div className="mt-5" id="pending-completion">
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <h3 className="fw-bold mb-0">
            Pending Completion
            {filteredPendingBookings.length > 0 && (
              <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.5em', verticalAlign: 'middle' }}>
                {filteredPendingBookings.length}
              </Badge>
            )}
          </h3>
          <div className="d-flex align-items-center gap-3">
            {filteredPendingBookings.length > 0 && (
              <small className="text-muted">
                <i className="fa fa-clock me-1" />
                Next auto-complete in
                {' '}
                {getTimeUntilAutoComplete()}
                {' '}
                (trips 48h+ past, nothing waiting for review)
              </small>
            )}
            <div className="btn-group">
              <button
                type="button"
                className={`btn btn-sm ${pendingFilter === 'review' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setPendingFilter('review')}
              >
                <i className="fa fa-envelope me-1" />
                Review Email
              </button>
              <button
                type="button"
                className={`btn btn-sm ${pendingFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setPendingFilter('all')}
              >
                All
              </button>
            </div>
          </div>
        </div>
        <TripClaimsRefusalAlert />
        {pendingLoading && (
          <div className="text-center py-4">
            <i className="fa fa-spin fa-spinner fa-2x text-muted" />
          </div>
        )}
        {!pendingLoading && filteredPendingBookings.length === 0 && (
          <p className="text-muted">
            {pendingFilter === 'review'
              ? 'No pending bookings with review email.'
              : 'No past confirmed transfers pending completion.'}
          </p>
        )}
        {!pendingLoading && filteredPendingBookings.length > 0 && (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Reference</th>
                  <th>Customer</th>
                  <th>Route</th>
                  <th>Pickup Time</th>
                  <th>Time Since Pickup</th>
                  <th>Operator</th>
                  <th style={{ width: '90px' }} className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredPendingBookings.map((booking) => (
                  // eslint-disable-next-line no-underscore-dangle
                  <tr key={booking._id}>
                    <td>
                      <NavLink
                        to={`/orders?quick_search=${encodeURIComponent(booking.booking_reference)}`}
                        className="text-decoration-none font-monospace small"
                      >
                        {booking.booking_reference}
                      </NavLink>
                    </td>
                    <td className="fw-semibold">{booking.full_name || '—'}</td>
                    <td className="small">
                      <div>{booking.destination_from?.address || '—'}</div>
                      <div className="text-muted">
                        <i className="fa fa-arrow-right me-1" style={{ fontSize: '0.7em' }} />
                        {booking.destination_to?.address || '—'}
                      </div>
                    </td>
                    <td className="small">{formatDateTime(booking.date_datetime, 'DD MMM HH:mm')}</td>
                    <td>
                      <Badge bg="secondary">
                        <i className="fa fa-clock me-1" />
                        {getTimeSincePickup(booking.date_datetime)}
                      </Badge>
                    </td>
                    <td className="small">{booking.operator?.name || '—'}</td>
                    {/* eslint-disable-next-line no-underscore-dangle */}
                    <td className="text-center">{renderCompleteActions(booking)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Active Offers Table */}
      <div className="mt-5">
        <h3 className="fw-bold mb-4">Active Leads &amp; Offers</h3>
        {offersLoading && (
          <div className="text-center py-4">
            <i className="fa fa-spin fa-spinner fa-2x text-muted" />
          </div>
        )}
        {!offersLoading && activeOffers.length === 0 && (
          <p className="text-muted">No sent, quoting or draft offers at the moment.</p>
        )}
        {!offersLoading && activeOffers.length > 0 && (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Reference</th>
                  <th>Title</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Offer Date</th>
                  <th>Valid Until</th>
                </tr>
              </thead>
              <tbody>
                {activeOffers.map((offer) => (
                  <tr key={offer.Id}>
                    <td>
                      <NavLink to="/service-offers" className="text-decoration-none font-monospace small">
                        {offer.referenceCode || '—'}
                      </NavLink>
                    </td>
                    <td className="fw-semibold">{offer.title}</td>
                    <td>{offer.recipient?.name || '—'}</td>
                    <td>{getStatusBadge(offer.status)}</td>
                    <td className="small text-muted">{formatOfferDate(offer.offerDate)}</td>
                    <td className="small text-muted">{formatOfferDate(offer.validUntil)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Home;
